import React, { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { TieredMenu } from 'primereact/tieredmenu';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useMountEffect } from 'primereact/hooks';
import StorageHelper from '../../utils/storageHelper';
import ChangeStatusCooperateReceiptDialog from '../../components/cooperateReceiptDialogs/ChangeStatusCooperateReceiptDialog';

const AllCooperateReceiptsTable = () => {
    const [receipts, setReceipts] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const dt = useRef(null);

    const cols = [
        { field: 'receiptNumber', header: 'Receipt Number' },
        { field: 'cooperateName', header: 'Corporate Name' },
        { field: 'modeOfPayment', header: 'Payment Mode' },
        { field: 'serialNumber', header: 'Serial Number' },
        { field: 'agentName', header: 'Agent Name' },
        { field: 'agentID', header: 'Agent ID' },
        { field: 'amount', header: 'Amount' },
        { field: 'currency', header: 'Currency' },
        { field: 'notes', header: 'Notes' },
        { field: 'status', header: 'Status' },
        { field: 'createdAt', header: 'Created At' },
        { field: 'revokedAt', header: 'Revoked At' }
    ];

    const exportColumns = cols.map((col) => ({ title: col.header, dataKey: col.field }));

    useMountEffect(() => {
        fetchReceipts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchReceipts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(process.env.REACT_APP_DOVESAPI + 'cooperateReceipt/all', {
                headers: {
                    Authorization: 'Bearer ' + StorageHelper.get('token')
                }
            });

            if (response.data.status === "SUCCESS") {
                setReceipts(response.data.responseBody);
            } else {
                console.error('Failed to fetch receipts:', response.data.status);
            }
        } catch (error) {
            console.error('Error fetching receipts:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({ selectionOnly });
    };

    const exportPdf = () => {
        import('jspdf').then((jsPDF) => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default();
                doc.autoTable(exportColumns, receipts);
                doc.save('corporate-receipts-report.pdf');
            });
        });
    };

    const exportExcel = () => {
        import('xlsx').then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(receipts);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: 'xlsx',
                type: 'array'
            });
            saveAsExcelFile(excelBuffer, 'corporate-receipts-report');
        });
    };

    const saveAsExcelFile = (buffer, fileName) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName + '.xlsx');
    };

    const openStatusDialog = (receipt) => {
        setSelectedReceipt(receipt);
        setShowStatusDialog(true);
    };

    const closeStatusDialog = () => {
        setShowStatusDialog(false);
        setSelectedReceipt(null);
    };

    const handleStatusUpdate = (receiptNumber, newStatus) => {
        // Update the receipt in the local state
        setReceipts(prevReceipts =>
            prevReceipts.map(receipt =>
                receipt.receiptNumber === receiptNumber
                    ? { ...receipt, status: newStatus }
                    : receipt
            )
        );
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    // Format amount with currency
    const amountBodyTemplate = (rowData) => {
        return `${rowData.currency || ''} ${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    // Format date
    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    // Status badge
    const statusBodyTemplate = (rowData) => {
        const getStatusStyle = (status) => {
            switch (status) {
                case 'PENDING':
                    return { color: '#FCB700', backgroundColor: '#FFF8E1' }; // warning

                case 'RECEIPTED':
                    return { color: '#00BAFE', backgroundColor: '#E6F7FF' }; // info

                case 'COMPLETED':
                    return { color: '#00D390', backgroundColor: '#E6FFF7' }; // success

                case 'REVOKE REQUESTED':
                    return { color: '#6C5CE7', backgroundColor: '#F3EFFF' }; // attention

                case 'FAILED':
                    return { color: '#FF6266', backgroundColor: '#FFECEC' }; // error → failed

                case 'REVOKED':
                    return { color: '#FF6266', backgroundColor: '#FFECEC' }; // error → revoked

                default:
                    return { color: '#A0A0A0', backgroundColor: '#F5F5F5' }; // neutral
            }
        };

        return (
            <span
                style={{
                    ...getStatusStyle(rowData.status),
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                }}
            >
                {rowData.status}
            </span>
        );
    };

    // Action button template
    const actionBodyTemplate = (rowData) => {
        return (
            <Button
                label="Change Status"
                outlined
                rounded
                onClick={() => openStatusDialog(rowData)}
                tooltip="Change Receipt Status"
                tooltipOptions={{ position: 'left' }}
                style={{
                    borderRadius: '5px',
                    whiteSpace: 'nowrap',
                }}
            />

        );
    };

    const globalFilterElement = (
        <input
            type="text"
            className="p-inputtext p-component"
            placeholder="Search receipts..."
            value={globalFilter}
            onChange={onGlobalFilter}
        />
    );

    const menu = useRef(null);
    const items = [
        {
            label: 'Download CSV',
            icon: 'pi pi-file-excel',
            command: () => { exportCSV(false); }
        },
        {
            label: 'Download PDF',
            icon: 'pi pi-file-pdf',
            command: () => { exportPdf(); }
        },
        {
            label: 'Download Excel',
            icon: 'pi pi-file-excel',
            command: () => { exportExcel(); }
        }
    ];

    return (
        <div className="card home-chart">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="card-header">
                <h2 className="card-title home-chart">All Corporate Receipts Report</h2>
                <div className="flex justify-content-end">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        {globalFilterElement}
                    </span>
                </div>
                <div id="report-select">
                    <TieredMenu model={items} popup ref={menu} breakpoint="767px" />
                    <Button
                        label="Download Report"
                        icon="pi pi-download"
                        onClick={(e) => menu.current.toggle(e)}
                    />
                </div>
            </div>

            <div className="card-body">
                <DataTable
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ref={dt}
                    value={receipts}
                    loading={loading}
                    tableStyle={{ minWidth: '50rem' }}
                    globalFilter={globalFilter}
                    emptyMessage="No corporate receipts found"
                >
                    <Column field="receiptNumber" header="Receipt Number" sortable style={{ minWidth: '150px' }} />
                    <Column field="cooperateName" header="Corporate Name" sortable style={{ minWidth: '200px' }} />
                    <Column field="modeOfPayment" header="Payment Mode" sortable style={{ minWidth: '150px' }} />
                    <Column field="serialNumber" header="Serial Number" sortable style={{ minWidth: '150px' }} />
                    <Column field="agentName" header="Agent Name" sortable style={{ minWidth: '180px' }} />
                    <Column field="agentID" header="Agent ID" sortable style={{ minWidth: '120px' }} />
                    <Column
                        field="amount"
                        header="Amount"
                        body={amountBodyTemplate}
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column field="notes" header="Notes" style={{ minWidth: '200px' }} />
                    <Column
                        field="status"
                        header="Status"
                        body={statusBodyTemplate}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="createdAt"
                        header="Created At"
                        body={(rowData) => dateBodyTemplate(rowData, 'createdAt')}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="revokedAt"
                        header="Revoked At"
                        body={(rowData) => dateBodyTemplate(rowData, 'revokedAt')}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        header="Actions"
                        body={actionBodyTemplate}
                        exportable={false}
                        style={{ minWidth: '100px', textAlign: 'center' }}
                    />
                </DataTable>
            </div>

            {/* Status Change Dialog */}
            <ChangeStatusCooperateReceiptDialog
                visible={showStatusDialog}
                receipt={selectedReceipt}
                onHide={closeStatusDialog}
                onStatusUpdate={handleStatusUpdate}
            />
        </div>
    );
};

export default AllCooperateReceiptsTable;